import {
  ReferenceField,
  REFERENCE_FIELDS,
  REFERENCE_TEMPLATE_TEXT_MAX,
  ReferenceTemplatePart,
  TEMPLATE_TEST_MAX_TEXT_LENGTH,
  TEMPLATE_TEST_MAX_TEXTS,
  TemplateTestResult,
} from '../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';

/*
 * Editor de formatos de referencia de protocolo (spec 009, US5, research R-06): tokenizar el texto de ejemplo, marcar
 * rangos de tokens como campo o tramo variable, armar las `parts` del contrato (MP 031 §3) y validarlas con las mismas
 * reglas que el servidor (`ProtocolReferenceTemplateCompiler.Validate`). Funciones puras, sin React.
 */

export type TemplateTokenKind = 'word' | 'separator';

/** Token del texto de ejemplo: palabra (letras / dígitos) o separador (todo lo demás, espacios incluidos). */
export interface TemplateToken {
  index: number;
  /** Posición en el texto (inclusive). */
  start: number;
  /** Posición en el texto (exclusive). */
  end: number;
  text: string;
  kind: TemplateTokenKind;
}

/** Rótulo de una marca: un campo o "Ignorar (variable)". */
export type MarkLabel = ReferenceField | 'any';
export const MARK_LABELS: MarkLabel[] = [...REFERENCE_FIELDS, 'any'];

/** Rango marcado de tokens (índices inclusive); las marcas no se solapan. */
export interface TemplateMark {
  from: number;
  to: number;
  label: MarkLabel;
}

export type TemplateValidationError = 'noText' | 'noProtocol' | 'duplicateField' | 'adjacentCaptures' | 'textTooLong';

const TOKEN_PATTERN = /[\p{L}\p{N}]+|[^\p{L}\p{N}]+/gu;
const WORD_PATTERN = /^[\p{L}\p{N}]/u;

/** Parte el texto en palabras y separadores (los separadores seguidos forman un solo token). */
export const tokenize = (text: string | null | undefined): TemplateToken[] => {
  const tokens: TemplateToken[] = [];
  if (!text) return tokens;
  for (const match of Array.from(text.matchAll(TOKEN_PATTERN))) {
    const start = match.index ?? 0;
    tokens.push({
      index: tokens.length,
      start,
      end: start + match[0].length,
      text: match[0],
      kind: WORD_PATTERN.test(match[0]) ? 'word' : 'separator',
    });
  }
  return tokens;
};

/** Rango ordenado entre dos índices (para la selección ancla / foco). */
export const rangeOf = (anchor: number, focus: number): { from: number; to: number } => ({
  from: Math.min(anchor, focus),
  to: Math.max(anchor, focus),
});

/** Marca que contiene el token, si hay. */
export const markAt = (marks: TemplateMark[], tokenIndex: number): TemplateMark | undefined =>
  marks.find((mark) => mark.from <= tokenIndex && tokenIndex <= mark.to);

const isField = (label: MarkLabel): label is ReferenceField => label !== 'any';

/**
 * Agrega una marca: reemplaza las que se solapan con el rango y, si es un campo, la marca anterior del mismo campo
 * (cada campo va una sola vez; "Ignorar" puede repetirse). Devuelve las marcas ordenadas.
 */
export const addMark = (marks: TemplateMark[], mark: TemplateMark): TemplateMark[] => {
  const range = rangeOf(mark.from, mark.to);
  const kept = marks.filter(
    (current) => (current.to < range.from || current.from > range.to) && !(isField(mark.label) && current.label === mark.label)
  );
  return [...kept, { ...range, label: mark.label }].sort((a, b) => a.from - b.from);
};

/** Quita la marca que contiene el token (sin cambios si no hay). */
export const removeMarkAt = (marks: TemplateMark[], tokenIndex: number): TemplateMark[] =>
  marks.filter((mark) => !(mark.from <= tokenIndex && tokenIndex <= mark.to));

const partOf = (label: MarkLabel): ReferenceTemplatePart => (label === 'any' ? { kind: 'any' } : { kind: 'field', field: label });

/** Partes de la plantilla: cada marca es un campo o un tramo variable; los tokens sin marcar seguidos, un texto fijo. */
export const buildParts = (tokens: TemplateToken[], marks: TemplateMark[]): ReferenceTemplatePart[] => {
  const parts: ReferenceTemplatePart[] = [];
  let text = '';
  const flush = () => {
    if (text) parts.push({ kind: 'text', value: text });
    text = '';
  };
  let index = 0;
  while (index < tokens.length) {
    const mark = markAt(marks, index);
    if (mark) {
      flush();
      parts.push(partOf(mark.label));
      index = Math.max(mark.to, index) + 1;
    } else {
      text += tokens[index].text;
      index++;
    }
  }
  flush();
  return parts;
};

/** Mismas reglas que el servidor; devuelve cada error una sola vez, en orden fijo. */
export const validateParts = (parts: ReferenceTemplatePart[]): TemplateValidationError[] => {
  const errors = new Set<TemplateValidationError>();
  const counts: Partial<Record<ReferenceField, number>> = {};
  let hasText = false;
  let textLength = 0;
  let previousCaptures = false;
  parts.forEach((part) => {
    const captures = part.kind !== 'text';
    if (captures && previousCaptures) errors.add('adjacentCaptures');
    previousCaptures = captures;
    if (part.kind === 'text') {
      textLength += part.value.length;
      if (part.value.trim()) hasText = true;
    } else if (part.kind === 'field') {
      counts[part.field] = (counts[part.field] ?? 0) + 1;
    }
  });
  const ordered: TemplateValidationError[] = [];
  if (!hasText) ordered.push('noText');
  if (!counts.protocol) ordered.push('noProtocol');
  if (REFERENCE_FIELDS.some((field) => (counts[field] ?? 0) > 1)) ordered.push('duplicateField');
  if (errors.has('adjacentCaptures')) ordered.push('adjacentCaptures');
  if (textLength > REFERENCE_TEMPLATE_TEXT_MAX) ordered.push('textTooLong');
  return ordered;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Texto fijo como lo compila el servidor: escapado y cada tramo de espacios vale por `\s+`. */
const textPattern = (value: string): string => value.split(/\s+/).map(escapeRegExp).join('\\s+');

/**
 * Marcas de una plantilla guardada sobre su texto de ejemplo (para editarla): arma la misma expresión que el servidor,
 * con cada parte como grupo, y ubica los campos y tramos variables en los tokens. Devuelve [] si el texto no coincide.
 */
export const marksFromParts = (sampleText: string | null | undefined, parts: ReferenceTemplatePart[]): TemplateMark[] => {
  if (!sampleText || parts.length === 0) return [];
  const pattern = parts
    .map((part, index) => {
      const last = index === parts.length - 1;
      if (part.kind === 'text') return `(${textPattern(part.value)})`;
      // Al final, el tramo variable o el campo llegan hasta el final del texto (el servidor no ancla el "any" final,
      // pero para ubicar la marca en el ejemplo se toma la cola entera).
      if (part.kind === 'any') return last ? '(.*?)\\s*$' : '(.*?)';
      return last ? '(.+?)\\s*$' : '(.+?)';
    })
    .join('');
  let match: RegExpExecArray | null;
  try {
    match = new RegExp(pattern, 'is').exec(sampleText);
  } catch {
    return [];
  }
  if (!match) return [];
  const tokens = tokenize(sampleText);
  const tokenAt = (position: number): number => {
    const found = tokens.find((token) => token.start <= position && position < token.end);
    return found ? found.index : tokens.length - 1;
  };
  const marks: TemplateMark[] = [];
  let position = match.index;
  parts.forEach((part, index) => {
    const length = match?.[index + 1]?.length ?? 0;
    if (part.kind !== 'text' && length > 0) {
      const label: MarkLabel = part.kind === 'any' ? 'any' : part.field;
      const from = tokenAt(position);
      const to = tokenAt(position + length - 1);
      if (!marks.some((mark) => !(mark.to < from || mark.from > to))) marks.push({ from, to, label });
    }
    position += length;
  });
  return marks;
};

/** Dos listas de partes iguales (para avisar si las marcas recuperadas no reproducen la plantilla guardada). */
export const sameParts = (a: ReferenceTemplatePart[], b: ReferenceTemplatePart[]): boolean =>
  a.length === b.length &&
  a.every((part, index) => {
    const other = b[index];
    if (part.kind !== other.kind) return false;
    if (part.kind === 'text') return other.kind === 'text' && part.value === other.value;
    if (part.kind === 'field') return other.kind === 'field' && part.field === other.field;
    return true;
  });

/** "Coincide en N de M". */
export const matchSummary = (results: TemplateTestResult[]): { matched: number; total: number } => ({
  matched: results.filter((result) => result.matched).length,
  total: results.length,
});

/**
 * Textos para la prueba en vivo: el de ejemplo primero y después los demás (nombres de los ítems), recortados, sin
 * repetir, sin vacíos ni textos de más de 2000 caracteres y hasta 500 (límites del servidor).
 */
export const templateTestTexts = (sample: string | null | undefined, others: Array<string | null | undefined> = []): string[] => {
  const texts: string[] = [];
  const seen = new Set<string>();
  [sample, ...others].forEach((value) => {
    const text = value?.trim();
    if (!text || text.length > TEMPLATE_TEST_MAX_TEXT_LENGTH || seen.has(text) || texts.length >= TEMPLATE_TEST_MAX_TEXTS) return;
    seen.add(text);
    texts.push(text);
  });
  return texts;
};

/** Textos escritos por el usuario en "Probar con un texto": uno por línea. */
export const textsFromLines = (value: string): string[] => templateTestTexts(undefined, value.split(/\r?\n/));

/** Partes normalizadas de una plantilla del servidor (tolera mayúsculas y campos sobrantes); descarta las inválidas. */
export const normalizeParts = (parts: unknown): ReferenceTemplatePart[] => {
  if (!Array.isArray(parts)) return [];
  const result: ReferenceTemplatePart[] = [];
  parts.forEach((raw: any) => {
    const kind = String(raw?.kind ?? raw?.Kind ?? '').trim().toLowerCase();
    if (kind === 'text') {
      const value = raw?.value ?? raw?.Value;
      if (typeof value === 'string') result.push({ kind: 'text', value });
    } else if (kind === 'field') {
      const field = String(raw?.field ?? raw?.Field ?? '').trim().toLowerCase() as ReferenceField;
      if (REFERENCE_FIELDS.includes(field)) result.push({ kind: 'field', field });
    } else if (kind === 'any') {
      result.push({ kind: 'any' });
    }
  });
  return result;
};

/** Partes como las espera el servidor (sin propiedades de más). */
export const partsForRequest = (parts: ReferenceTemplatePart[]): ReferenceTemplatePart[] =>
  parts.map((part) => {
    if (part.kind === 'text') return { kind: 'text', value: part.value };
    if (part.kind === 'field') return { kind: 'field', field: part.field };
    return { kind: 'any' };
  });

/** Resumen legible de las partes: "Protocolo {protocol} - {paciente} …" con los rótulos dados. */
export const partsPreview = (parts: ReferenceTemplatePart[], labelOf: (label: MarkLabel) => string): string =>
  parts
    .map((part) => {
      if (part.kind === 'text') return part.value;
      return `[${labelOf(part.kind === 'any' ? 'any' : part.field)}]`;
    })
    .join('');

/** Mueve un elemento de la lista una posición (subir = -1, bajar = +1); sin cambios en los bordes. */
export const moveItem = <T>(list: T[], index: number, delta: -1 | 1): T[] => {
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return list;
  const copy = [...list];
  [copy[index], copy[target]] = [copy[target], copy[index]];
  return copy;
};
